<template>
	<div>
		<PageHeader
			:data="{
				name: 'Invoices',
				breadcrumbs: [
					{ name: 'Home', to: '/' },
					{ name: 'Clients', to: '/clients' },
					{ name: client.name, to: '/clients/edit/' + client.id },
					{ name: 'Billings', to: '/clients/edit/' + client.id + '/billing' },
				],
			}"
		/>

		<div class="card">
			<div class="card-header">
				<div class="actions d-flex justify-content-between align-items-center">
					<h5 class="p-0 m-0">Invoices</h5>
					<input
						type="text"
						class="form-control w-25"
						v-model="request.search"
						placeholder="enter name, email or phone"
						@change="searchInvoices()"
					/>
				</div>
			</div>
			<div class="card-body p-3">
				<table class="table table-hover">
					<thead>
						<tr>
							<th width="100">Invoice Id</th>
							<th>Data</th>
							<th>Description</th>
							<th>Status</th>
							<th>Payment Status</th>
							<th>Amount</th>
							<th width="100">Due Date</th>
							<th width="150">Actions</th>
						</tr>
					</thead>
					<tbody v-if="invoices.length > 0 && !loading">
						<tr
							v-for="invoice in invoices"
							:key="invoice.id"
							:class="[{ 'bg-danger-lighter': invoice.v3_status == 'overdue' }]"
						>
							<td class="align-middle">
								{{ invoice.invoice_id }}
							</td>
							<td class="align-middle">
								<div class="data">
									<div :class="[{ 'text-danger fw-bold': invoice.v3_status == 'overdue' }]">
										{{ invoice.organization }}
									</div>
									<div :class="[{ 'text-danger fw-bold': invoice.v3_status == 'overdue' }]">
										{{ invoice.first_name }} {{ invoice.last_name }}
									</div>
									<div
										class="d-flex justify-content-start align-items-center"
										:class="[{ 'text-danger fw-bold': invoice.v3_status == 'overdue' }]"
									>
										{{ invoice.email }}
									</div>
								</div>
							</td>
							<td class="align-middle">
								<span v-if="invoice.v3_status == 'overdue'" class="text-danger fw-bold">{{
									invoice.description
								}}</span>
								<span v-else>{{ invoice.description }}</span>
							</td>
							<td class="align-middle">
								<span
									class="badge badge-primary"
									:class="[{ 'badge-success': invoice.display_status == 'viewed' }]"
								>
									<div class="d-flex justify-content-between align-items-center">
										<span class="me-2 text-uppercase">{{ invoice.display_status }}</span>
										<font-awesome-icon
											class="fs-6"
											:icon="[
												'fas',
												invoice.display_status == 'viewed' ? 'envelope-open' : 'envelope',
											]"
										/>
									</div>
								</span>
							</td>
							<td class="align-middle">
								<span
									class="badge badge-primary"
									:class="[
										{
											'badge-success': invoice.payment_status == 'paid',
											'badge-danger': invoice.v3_status == 'overdue',
										},
									]"
								>
									<div class="d-flex justify-content-between align-items-center">
										<span class="me-2 text-uppercase">{{ invoice.payment_status }}</span>
										<font-awesome-icon
											class="fs-6"
											:icon="[
												'fas',
												invoice.payment_status == 'paid' ? 'check-square' : 'money-check-alt',
											]"
										/>
									</div>
								</span>
							</td>

							<td class="align-middle text-center">
								<span v-if="invoice.v3_status == 'overdue'" class="badge badge-danger p-1 d-block">
									<div class="d-flex flex-column justify-content-between align-items-center">
										<span class="">Overdue {{ invoice.overdue_date }} days!</span>
									</div>
								</span>
								<span class="me-2 text-uppercase text-center">{{ invoice.due_date }}</span>
							</td>
							<td class="align-middle">
								<font-awesome-icon :icon="['fas', 'dollar-sign']" class="text-success" />
								{{ invoice.amount }}
							</td>
							<td class="align-middle">
								<router-link
									:to="
										'/clients/edit/' +
											client.id +
											'/billing/email-notifications?invoice=' +
											invoice.id
									"
									v-if="invoice.payment_status != 'paid' && invoice.payment_status !== 'viewed'"
									class="btn btn-primary btn-sm me-2"
									title="Send Email Notification"
								>
									<font-awesome-icon :icon="['fas', 'at']" />
								</router-link>
								<router-link
									:to="
										'/clients/edit/' +
											client.id +
											'/billing/letter-notifications?invoice=' +
											invoice.id
									"
									v-if="invoice.payment_status != 'paid' && invoice.payment_status !== 'viewed'"
									class="btn btn-primary btn-sm me-2"
									title="Send Letter Notification"
								>
									<font-awesome-icon :icon="['fas', 'envelope']" />
								</router-link>
								<button class="btn btn-info btn-sm" title="More Details">
									<font-awesome-icon :icon="['fas', 'eye']" />
								</button>
							</td>
						</tr>
					</tbody>
					<tbody v-if="invoices.length === 0 && !loading">
						<tr>
							<td colspan="5" class="text-center">
								<span class="fw-normal">There is not invoices for this client</span>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div class="px-3">
				<!-- Pagination -->
				<div v-if="pagination" class="d-flex justify-content-between align-items-center">
					<!-- Items Statistic -->
					<div class="items-statistic">
						From
						{{ pagination.from }}
						to
						{{ pagination.to }}
						of
						{{ pagination.total }}
					</div>
					<!-- Pages navigation -->
					<div v-if="pagination.last_page > 1">
						<nav>
							<ul class="pagination" v-if="pagination.links && pagination.links.length > 0">
								<li
									:key="'pag-' + index"
									v-for="(link, index) in pagination.links"
									class="page-item"
									:class="link.active ? 'active' : ''"
								>
									<a role="button" :disabled="true" class="page-link" @click="paginate(link.url)">{{
										link.label | removeString
									}}</a>
								</li>
							</ul>
						</nav>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader'
import { mapState } from 'vuex'
import { handleErrors } from '../../actions/helpers'
import { Tooltip } from 'bootstrap'
import restApi from '../../api/index.js'
export default {
	name: 'ClientInvoices',
	components: { PageHeader },
	data() {
		return {
			loading: false,
			client: {
				name: '',
			},
			invoices: [],
			pagination: null,
			request: {
				page: this.$route.query.page | 1,
				client_id: this.$route.params.id,
				search: '',
			},
		}
	},
	computed: {
		...mapState(['authUser', 'storageUrl']),
	},
	created() {
		setTimeout(() => {
			document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(el => {
				new Tooltip(el)
			})
		}, 1100)
		this.getClient()
		this.getInvoices()
	},
	methods: {
		// Get Client
		getClient() {
			restApi
				.get(`/clients/${this.$route.params.id}`)
				.then(({ data }) => {
					this.client = data
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Get Invoices
		getInvoices() {
			this.loading = true
			restApi
				.get(`/freshbooks/invoices`, { params: this.request })
				.then(({ data }) => {
					this.invoices = data.data
					this.pagination = data
					delete this.pagination.data
					if (!this.compareRequest()) {
						this.$router.push({ path: this.$router.currentPath, query: this.request })
					}
					this.loading = false
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
					this.loading = false
				})
		},

		// Compare request to avoid redirect to same page
		compareRequest() {
			let isEqual = true
			for (const property in this.request) {
				if (this.$route.query[property] != this.request[property]) {
					isEqual = false
				}
			}
			return isEqual
		},

		searchInvoices() {
			this.getInvoices()
		},

		// Paginate - when click on paginate buttons
		paginate(url) {
			let page = 1
			page = url.split('?')[1].replace('page=', '')
			this.request.page = page
			this.getInvoices()
		},
	},
	filters: {
		removeString: function(value) {
			if (!value) return ''
			value = value.replace('&laquo;', '')
			return value.replace('&raquo;', '')
		},
	},
}
</script>
